import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";

import CreditNoteFormFields from "./CreditNoteFormFields";
import CreditNoteInvoiceItemGroup from "./CreditNoteInvoiceItemGroup";
import httpRequest from "../utils/httpRequest";

const CreateCreditNoteInvoiceDialog = ({ open, onClose }) => {
  const [customers, setCustomers] = useState([]);
  const [firms, setFirms] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);

  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedFirm, setSelectedFirm] = useState("");
  const [selectedFY, setSelectedFY] = useState("");

  const [invoices, setInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [editableItems, setEditableItems] = useState([]);

  const [reason, setReason] = useState("");

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    if (open) {
      (async () => {
        const [c, f, fy] = await Promise.all([
          httpRequest("customers/getAll", "GET"),
          httpRequest("firm/getAll", "GET"),
          httpRequest("invoice/getAllFinancialYears", "GET"),
        ]);
        setCustomers(c);
        setFirms(f);
        setFinancialYears(fy);
      })();
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSelectedCustomer("");
      setSelectedFirm("");
      setSelectedFY("");
      setInvoices([]);
      setSelectedInvoices([]);
      setInvoiceItems([]);
      setEditableItems([]);
      setReason("");
    }
  }, [open]);

  useEffect(() => {
    const fetchInvoices = async () => {
      if (selectedCustomer && selectedFirm && selectedFY) {
        try {
          const data = await httpRequest(
            `invoice/getInvoicesByCustomerIdEligibleForCN/${selectedCustomer}`,
            "GET",
            {},
            {
              firm_id: selectedFirm,
              financial_year_id: selectedFY,
            }
          );
          setInvoices(data);
          setSelectedInvoices([]);
          setInvoiceItems([]);
          setEditableItems([]);
        } catch (err) {
          console.error("Failed to fetch invoices:", err);
        }
      }
    };

    fetchInvoices();
  }, [selectedCustomer, selectedFirm, selectedFY]);

  const handleCustomerChange = (id) => {
    setSelectedCustomer(id);
  };

  const handleInvoiceSelection = async (selected) => {
    setSelectedInvoices(selected);
    const items = await httpRequest("invoice/getByInvoiceIds", "POST", {
      invoice_ids: selected,
    });
    setInvoiceItems(items);
    setEditableItems(
      items.map((i) => ({
        ...i,
        returned_quantity: i.quantity,
        returned_pcs: i.pcs,
      }))
    );
  };

  const handleRemoveItem = (itemId) => {
    setEditableItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const handleItemEdit = (itemId, field, value) => {
    setEditableItems((prev) =>
      prev.map((i) => (i.id === itemId ? { ...i, [field]: value } : i))
    );
  };

  const handleSubmit = async () => {
    const payload = {
      customer_id: selectedCustomer,
      firm_id: selectedFirm,
      financial_year_id: selectedFY,
      reason,
      type: "gst",
      invoice_ids: selectedInvoices,
      items: editableItems.map((item) => ({
        invoice_item_id: item.id,
        returned_quantity: item.returned_quantity,
        returned_quantity_unit: item.unit,
        rate: item.rate,
        product_name: item.product_name,
        hsn: item.hsn,
        discount:
          item.quantity && item.rate
            ? (item.discount / (item.quantity * item.rate)) *
              item.returned_quantity *
              item.rate
            : 0,
      })),
    };

    try {
      await httpRequest("/credit_note/create", "POST", payload);
      setSnackbarMessage("Credit Note created successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      onClose();
    } catch (error) {
      console.error("Error creating credit note:", error);
      setSnackbarMessage("Failed to create Credit Note!");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const isReasonVisible =
    selectedInvoices.length > 0 &&
    editableItems.every((item) => item.returned_quantity > 0);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Create Credit Note</DialogTitle>
        <DialogContent dividers>
          <CreditNoteFormFields
            firms={firms}
            financialYears={financialYears}
            customers={customers}
            selectedFirm={selectedFirm}
            selectedFY={selectedFY}
            selectedCustomer={selectedCustomer}
            setSelectedFirm={setSelectedFirm}
            setSelectedFY={setSelectedFY}
            handleCustomerChange={handleCustomerChange}
            invoices={invoices}
            selectedInvoices={selectedInvoices}
            handleInvoiceSelection={handleInvoiceSelection}
          />
          <Box mt={3}>
            <CreditNoteInvoiceItemGroup
              selectedInvoices={selectedInvoices}
              invoices={invoices}
              editableItems={editableItems}
              onEdit={handleItemEdit}
              onRemove={handleRemoveItem}
            />
          </Box>
          {isReasonVisible && (
            <Box mt={3}>
              <TextField
                fullWidth
                label="Reason for Credit Note"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                multiline
                rows={3}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            Create Credit Note
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CreateCreditNoteInvoiceDialog;